import { useCallback, useState } from 'react';

import { Field, FieldDescription, FieldError } from '@/components/design-system/field';
import InputWithIcon from '@/components/ui/InputWithIcon';
import type { TranslationKey } from '@/i18n/i18n';
import { Eye, EyeOff } from 'lucide-react';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormFieldLabel } from './FormFieldLabel';

type IconComponent = React.ComponentType<{ className?: string }>;
type InputStatus = 'default' | 'error' | 'success';

interface FormPasswordProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  id?: string;
  name: FieldPath<TFieldValues>;
  label?: TranslationKey;
  placeholder?: TranslationKey;
  description?: TranslationKey;
  tooltip?: TranslationKey;
  autoComplete?: 'new-password' | 'current-password';
  isRequired?: boolean;
  leftIcon?: IconComponent;
  status?: InputStatus;
  hideErrorMessage?: boolean;
}

export const FormPassword = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  tooltip,
  autoComplete = 'current-password',
  isRequired = false,
  leftIcon,
  status,
  hideErrorMessage = false
}: FormPasswordProps<TFieldValues>) => {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();

  const handleShowPassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          {label && (
            <FormFieldLabel label={label} tooltip={tooltip} isRequired={isRequired} fieldError={fieldState.error} />
          )}
          <InputWithIcon
            {...field}
            status={status ?? (fieldState.error ? 'error' : 'default')}
            type={showPassword ? 'text' : 'password'}
            placeholder={placeholder ? t(placeholder) : undefined}
            autoComplete={autoComplete}
            leftIcon={leftIcon}
            rightIcon={showPassword ? EyeOff : Eye}
            onRightIconClick={handleShowPassword}
            className="w-full"
            aria-invalid={fieldState.invalid}
          />
          {description && <FieldDescription className="text-sm text-neutral-500">{t(description)}</FieldDescription>}
          {!hideErrorMessage && <FieldError errors={fieldState.error ? [fieldState.error] : undefined} />}
        </Field>
      )}
    />
  );
};
